@use '@/styles/tokens' as *;

/* Header cluster (rendered inside the drawer's cream title band) */
.header {
  display: inline-flex;
  align-items: center;
  gap: var(--space-3);
}

.iconBadge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  border-radius: 999px;
  background: $color-brand-700;
  color: var(--text-inverse);
}

.headerText {
  display: inline-flex;
  flex-direction: column;
  gap: 2px;
}

.headerHeading {
  font-family: $font-family-base;
  font-size: 18px;
  font-weight: $font-weight-medium;
  letter-spacing: -0.02em;
  color: $color-neutral-900;
}

.headerSubtitle {
  font-size: 13px;
  color: $color-neutral-950;
}

/* Subtitle row: orderNumber + status chip side-by-side */
.headerSubtitleRow {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
}

/* Order metadata card: 2×2 grid with per-row vertical dividers */
.infoCard {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: var(--space-5);
  row-gap: var(--space-4);
  margin: 0 0 var(--space-5);
  padding: var(--space-4);
  background: var(--surface-subtle);
  border-radius: 12px;
  border: 1px solid var(--border-subtle);
}

.infoItem {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-width: 0;

  /* Left-column cells (1st, 3rd) carry the divider to the right column. */
  &:nth-child(odd) {
    padding-inline-end: var(--space-5);
    border-inline-end: 1px solid $color-neutral-200;
  }
}

/* Address spans both columns and drops its right divider (5th, full-width cell). */
.infoItemFull {
  grid-column: 1 / -1;
}
.infoItem.infoItemFull {
  padding-inline-end: 0;
  border-inline-end: none;
}

.infoLabel {
  color: $color-neutral-600;
  font-size: 13px;
}

.infoValue {
  color: $color-neutral-950;
  font-size: 14px;
}

.deviceInfo {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.deviceName {
  overflow: hidden;
  color: $color-neutral-900;
  font-size: 15px;
  font-weight: $font-weight-medium;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.deviceSubtitle {
  color: $color-neutral-500;
  font-size: 13px;
}

/* Rejected banner: Figma's red error treatment wrapping the actual rejection reason (ADR-0018) */
.rejection {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  margin: 0 0 var(--space-5);
  padding: var(--space-4);
  background: $color-error-50;
  border-radius: 12px;
}

.rejectionHeading {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  color: $color-stage-attention-text;
  font-size: 14px;
  font-weight: $font-weight-regular;
}

.rejectionValue {
  margin: 0;
  color: $color-error-950;
  font-size: 12px;
  font-weight: $font-weight-regular;
  overflow-wrap: break-word;
}

/* ── Replacement-drawer deltas ─────────────────────────────────────────── */

/* Reason + description card (Figma 429:293402): white, subtle border, 10px radius */
.reasonCard {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  margin: 0 0 var(--space-5);
  padding: 19px 17px;
  background: var(--surface-card);
  border: 1px solid var(--border-subtle);
  border-radius: 10px;
}
.reasonHeading {
  margin: 0;
  color: $color-neutral-700;
  font-size: 15px;
  font-weight: $font-weight-medium;
}
/* Reason description body (Geist 14/R, Text/Tertiary #555) */
.description {
  margin: 0;
  color: $color-neutral-700;
  font-size: 14px;
  line-height: 1.5;
  overflow-wrap: break-word;
}

/* Activity Timeline heading (Figma 429:293438: Geist 14/R, muted slate) */
.timelineHeading {
  margin: var(--space-2) 0 var(--space-4);
  color: $color-neutral-600;
  font-size: 14px;
  font-weight: $font-weight-regular;
}

/* Approved: replacement-order band (Figma 429:295053) */
.orderBand {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  margin: 0 0 var(--space-5);
  padding: var(--space-2) var(--space-3);
  background: var(--surface-subtle);
  border-radius: 12px;
}
.orderBandText {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}
.orderBandCaption {
  color: $color-neutral-600;
  font-size: 12px;
}
.orderBandNumber {
  color: $color-neutral-950;
  font-size: 14px;
  font-weight: $font-weight-medium;
}

/* Device header row: name/category left, warranty chip right */
.deviceHeader {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-3);
  margin: 0 0 var(--space-3);
}

/* Attachments thumbnail grid (the preview overlay lives in the shared ImageLightbox) */
.thumbGrid {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-4);
  margin: 0 0 var(--space-5);
  padding: 0;
  list-style: none;
}
.thumbBtn {
  width: 84px;
  height: 84px;
  padding: 0;
  border: 1px solid $color-neutral-200;
  border-radius: 8px;
  background: $color-neutral-100;
  overflow: hidden;
  cursor: pointer;
  &:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }
}
.thumbImg {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
